import { useAuth } from "@hooks/useAuth";
import Link from "next/link";
import { ReactNode } from "react";
import { Location } from "types";

type LocationsGateProps = {
  children: ReactNode;
} & ({
  where: (locationIds: string[]) => boolean;
} | {
  has: string;
})

export const LocationsGate: React.FC<LocationsGateProps> = ({
  children,
  ...props
}) => {
  const { whereLocationPermitted } = useLocationRender()

  if ("where" in props)
    return whereLocationPermitted(children, props.where) ?? null;

  return whereLocationPermitted(children, ls => ls.includes(props.has)) ?? null;
};

export function useLocationRender() {
  const { user, organization } = useAuth();
  const member = organization?.members?.find(m => m.id === user?.id);
  const assignedLocationIds = member?.locations ?? [];

  function getAccessibleLocations(allLocations: Location[]) {
    if (user?.system_role === "admin") return allLocations;
    if (organization?.created_by === user?.id) return allLocations;
    if (assignedLocationIds.includes("**all**")) return allLocations;
    if (user?.system_permissions.includes("admin:read_all_organizations") && user?.system_permissions.includes("locations:view")) return allLocations;

    return allLocations.filter(loc => loc.created_by === user?.id || assignedLocationIds.includes(loc.id));
  };

  function _hasLocationAccess(transformer: (ls: string[]) => boolean) {
    if (user?.system_role === "admin") return true;
    if (organization?.created_by === user?.id) return true;
    return transformer(assignedLocationIds);
  }

  function ifLocationPermitted<
    T,
    TOrElseFunc extends ((...args: any[]) => any) | undefined
  >(
    locationId: string,
    obj: T,
    orElse?: TOrElseFunc
  ): T | (TOrElseFunc extends undefined ? undefined : ReturnType<NonNullable<TOrElseFunc>>) {
    if (_hasLocationAccess(ls => ls?.includes(locationId))) return obj;
    return orElse?.();
  }

  function whereLocationPermitted<T>(obj: T, test: (locations: string[]) => boolean) {
    if (_hasLocationAccess(test)) return obj;
  }

  return {
    ifLocationPermitted,
    whereLocationPermitted,
    getAccessibleLocations,
    assignedLocationIds,
  }
}

// Helper component for conditional links based on location access
export function LinkIfLocation({ _if, ...props }: React.PropsWithChildren<Parameters<typeof Link>[0] & { _if: string }>) {
  const { ifLocationPermitted } = useLocationRender()
  return ifLocationPermitted(_if, <Link {...props} />, () => props.children)
}
